import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import { Form } from '@unform/web';
import { assign, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, Select } from '~/components/Form';
import { t } from '~/helpers';
import { validationErrors, validation } from '~/helpers/yupValidations';
import {
  storeData,
  updateData,
} from '~/store/modules/conversorEntradaSaida/actions';

import IOEntrada from '../components/IOEntrada';
import IOSaida from '../components/IOSaida';
import * as S from './styles';

export default function RequestForm({ reference, create, data }) {
  const dispatch = useDispatch();

  const { loading, find, params } = useSelector(
    (state) => state.conversorEntradaSaida
  );

  const [iosEntrada, setIosEntrada] = useState([]);
  const [iosSaida, setIosSaida] = useState([]);
  const [postoSelecionado, setPostoSelecionado] = useState(
    data?.idposto || find?.idposto || null
  );

  const initialData = useMemo(
    () => (create ? assign({}, data) : assign(find || {}, data)),
    [create, data, find]
  );

  const idPostoAtual = postoSelecionado || initialData?.idposto;

  const createIosDefault = useCallback(
    (tipoIO) =>
      Array.from({ length: 6 }, (_, index) => {
        const base = {
          id: `${tipoIO}-${index}`,
          idconversor: initialData?.idconversor || '',
          idposto: idPostoAtual || '',
          idcliente: initialData?.idcliente || '',
          tipoconversorio: tipoIO,
          portaconversorio: index,
          nomeconversorio: '',
          alarmes: [],
        };

        if (tipoIO === 'E') {
          return {
            ...base,
            idtanquefisico: null,
            tanques: [],
          };
        }

        return {
          ...base,
          modosegurancaconversorio: null,
          modooperacaoconversorio: null,
          tipodesativacaoconversorio: null,
          alarmes: [],
        };
      }),
    [initialData, idPostoAtual]
  );

  const isFilled = useCallback(
    (value) => value !== null && value !== undefined && value !== '',
    []
  );

  const isTextFilled = useCallback(
    (value) => isFilled(value) && String(value).trim() !== '',
    [isFilled]
  );

  const hasItems = useCallback((items = []) => !isEmpty(items), []);

  const isEntradaConfigurada = useCallback(
    (io) => isTextFilled(io.nomeconversorio) || hasItems(io.tanques),
    [hasItems, isTextFilled]
  );

  const isSaidaConfigurada = useCallback(
    (io) =>
      isTextFilled(io.nomeconversorio) ||
      isFilled(io.modosegurancaconversorio) ||
      isFilled(io.modooperacaoconversorio) ||
      isFilled(io.tipodesativacaoconversorio) ||
      hasItems(io.alarmes),
    [hasItems, isFilled, isTextFilled]
  );

  const validaIos = useCallback(() => {
    const errors = [];

    iosEntrada.filter(isEntradaConfigurada).forEach((io) => {
      if (!isTextFilled(io.nomeconversorio)) {
        errors.push(
          `IO de Entrada ${io.portaconversorio}: Nome do IO e obrigatorio.`
        );
      }

      if (!hasItems(io.tanques)) {
        errors.push(
          `IO de Entrada ${io.portaconversorio}: selecione ao menos um tanque.`
        );
      }
    });

    iosSaida.filter(isSaidaConfigurada).forEach((io) => {
      if (!isTextFilled(io.nomeconversorio)) {
        errors.push(
          `IO de Saida ${io.portaconversorio}: Nome do IO e obrigatorio.`
        );
      }

      if (!isFilled(io.modosegurancaconversorio)) {
        errors.push(
          `IO de Saida ${io.portaconversorio}: Modo Seguranca e obrigatorio.`
        );
      }

      if (!isFilled(io.modooperacaoconversorio)) {
        errors.push(
          `IO de Saida ${io.portaconversorio}: Modo Operacao e obrigatorio.`
        );
      }

      if (!isFilled(io.tipodesativacaoconversorio)) {
        errors.push(
          `IO de Saida ${io.portaconversorio}: Tipo Desativacao e obrigatorio.`
        );
      }

      if (!hasItems(io.alarmes)) {
        errors.push(
          `IO de Saida ${io.portaconversorio}: associe ao menos um alarme.`
        );
      }
    });

    errors.forEach((error) => toast.error(error));

    return isEmpty(errors);
  }, [
    hasItems,
    iosEntrada,
    iosSaida,
    isEntradaConfigurada,
    isFilled,
    isSaidaConfigurada,
    isTextFilled,
  ]);

  const getIdTanque = useCallback(
    (tanque) => {
      const value = tanque?.idtanquefisico || tanque?.value || tanque;

      return isFilled(value) ? Number(value) : NaN;
    },
    [isFilled]
  );

  const getIdConfigAlarmeTanque = useCallback(
    (alarme) => {
      const value = alarme?.idconfigalarmetanque || alarme?.value || alarme;

      return isFilled(value) ? Number(value) : NaN;
    },
    [isFilled]
  );

  const normalizaAlarmes = useCallback(
    (alarmes = []) =>
      alarmes
        .map((alarme) => ({
          ...(typeof alarme === 'object' ? alarme : {}),
          idconfigalarmetanque: getIdConfigAlarmeTanque(alarme),
        }))
        .filter((alarme) => !Number.isNaN(alarme.idconfigalarmetanque)),
    [getIdConfigAlarmeTanque]
  );

  const normalizaTanques = useCallback(
    (tanques = []) =>
      tanques
        .map((tanque) => ({
          ...(typeof tanque === 'object' ? tanque : {}),
          idtanquefisico: getIdTanque(tanque),
          alarmes: normalizaAlarmes(tanque?.alarmes || []),
        }))
        .filter((tanque) => !Number.isNaN(tanque.idtanquefisico)),
    [getIdTanque, normalizaAlarmes]
  );

  const mergeIosComDefault = useCallback(
    (tipoIO, configurados = []) => {
      const defaults = createIosDefault(tipoIO);

      return defaults.map((itemDefault) => {
        const configurado = configurados.find(
          (item) =>
            Number(item.portaconversorio) ===
            Number(itemDefault.portaconversorio)
        );

        if (!configurado) return itemDefault;

        return {
          ...itemDefault,
          ...configurado,
          id: `${tipoIO}-${configurado.portaconversorio}`,
          idposto: idPostoAtual,
          tanques:
            tipoIO === 'E'
              ? normalizaTanques(
                  configurado.tanques ||
                    configurado.tanquesfisicos ||
                    (configurado.idtanquefisico ? [configurado] : [])
                )
              : [],
          alarmes:
            tipoIO === 'S' ? normalizaAlarmes(configurado.alarmes || []) : [],
        };
      });
    },
    [createIosDefault, idPostoAtual, normalizaTanques, normalizaAlarmes]
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const schemaShape = {
          idposto: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.informeumnumero')),

          nomeconversor: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),

          ipconversor: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),

          portaconversor: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.informeumnumero')),
        };

        if (!create) {
          schemaShape.idconversor = Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.informeumnumero'));
        }

        const schema = Yup.object().shape(schemaShape);

        await validation(schema, values);

        if (!validaIos()) return;

        const normalizaEntrada = (io) => {
          const entrada = {
            idposto: Number(values.idposto),
            idcliente: Number(initialData?.idcliente || values.idcliente || 0),
            tipoconversorio: 'E',
            portaconversorio: Number(io.portaconversorio),
            nomeconversorio: io.nomeconversorio || '',
            tanques: normalizaTanques(io.tanques || []).map((tanque) => ({
              idtanquefisico: tanque.idtanquefisico,
              alarmes: normalizaAlarmes(tanque.alarmes || []).map((alarme) => ({
                idconfigalarmetanque: alarme.idconfigalarmetanque,
              })),
            })),
          };

          if (!create) {
            entrada.idconversor = Number(values.idconversor);
          }

          return entrada;
        };

        const normalizaSaida = (io) => {
          const saida = {
            idposto: Number(values.idposto),
            idcliente: Number(initialData?.idcliente || values.idcliente || 0),
            tipoconversorio: 'S',
            portaconversorio: Number(io.portaconversorio),
            nomeconversorio: io.nomeconversorio || '',

            modosegurancaconversorio: isFilled(io.modosegurancaconversorio)
              ? Number(io.modosegurancaconversorio)
              : 0,

            modooperacaoconversorio: isFilled(io.modooperacaoconversorio)
              ? Number(io.modooperacaoconversorio)
              : null,

            tipodesativacaoconversorio: isFilled(io.tipodesativacaoconversorio)
              ? Number(io.tipodesativacaoconversorio)
              : null,

            alarmes: normalizaAlarmes(io.alarmes || []).map((alarme) => ({
              idconfigalarmetanque: alarme.idconfigalarmetanque,
            })),
          };

          if (!create) {
            saida.idconversor = Number(values.idconversor);
          }

          return saida;
        };

        const entradas = iosEntrada
          .filter(isEntradaConfigurada)
          .map(normalizaEntrada);

        const saidas = iosSaida.filter(isSaidaConfigurada).map(normalizaSaida);

        const formData = {
          ...initialData,
          ...values,
          nomeconversor: values.nomeconversor,
          idposto: Number(values.idposto),
          idcliente: Number(initialData?.idcliente || values.idcliente || 0),
          portaconversor: Number(values.portaconversor),
          entradas,
          saidas,
        };

        if (!create) {
          formData.idconversor = Number(values.idconversor);
        }

        if (create) dispatch(storeData(formData, params));
        else dispatch(updateData(formData, params));
      } catch (error) {
        validationErrors(error, reference);
      }
    },
    [
      reference,
      initialData,
      create,
      dispatch,
      params,
      iosEntrada,
      iosSaida,
      isFilled,
      isEntradaConfigurada,
      isSaidaConfigurada,
      validaIos,
      getIdTanque,
      getIdConfigAlarmeTanque,
      normalizaTanques,
      normalizaAlarmes,
    ]
  );

  if (loading) {
    return (
      <S.WrapperLoading>
        <Spinner loading />
      </S.WrapperLoading>
    );
  }

  return (
    <Form ref={reference} initialData={initialData} onSubmit={handleSubmit}>
      {!create && (
        <Input
          name="idconversor"
          label="Código do Conversor"
          type="number"
          disabled
        />
      )}

      <Autocomplete
        name="idposto"
        label="nomeposto"
        url="postos"
        unique="idposto"
        title="Posto/Comboio"
        disabled={!create}
        getValue={(value) => {
          setPostoSelecionado(value?.idposto || value);
        }}
      />

      <Select
        name="nomeconversor"
        label="Nome do Conversor"
        options={[
          {
            value: 'Moxa',
            label: 'Moxa',
          },
        ]}
      />

      <Input name="ipconversor" label="IP do Conversor" />

      <Input name="portaconversor" label="Porta do Conversor" type="number" />

      <Accordion expanded>
        <AccordionSummary id="panel-io-entrada">
          <Typography>IO de Entrada</Typography>
        </AccordionSummary>

        <AccordionDetails>
          <IOEntrada
            key={`entrada-${idPostoAtual || 'sem-posto'}`}
            idposto={idPostoAtual}
            onCallback={setIosEntrada}
            data={
              create
                ? createIosDefault('E')
                : mergeIosComDefault('E', find?.entradas || [])
            }
          />
        </AccordionDetails>
      </Accordion>

      <Accordion expanded>
        <AccordionSummary id="panel-io-saida">
          <Typography>IO de Saída</Typography>
        </AccordionSummary>

        <AccordionDetails>
          <IOSaida
            key={`saida-${idPostoAtual || 'sem-posto'}`}
            idposto={idPostoAtual}
            onCallback={setIosSaida}
            data={
              create
                ? createIosDefault('S')
                : mergeIosComDefault('S', find?.saidas || [])
            }
          />
        </AccordionDetails>
      </Accordion>
    </Form>
  );
}

RequestForm.propTypes = {
  reference: PropTypes.shape({ current: PropTypes.shape({}) }).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    idposto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

RequestForm.defaultProps = {
  data: {},
};
